import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of, defer } from 'rxjs';
import { map, take, shareReplay } from 'rxjs/operators';
import { UserDataService } from '../../../shared/services/userData.service';
import { RolesService } from '../../../shared/services/roles.service';
import { Role } from '../../../shared/interfaces/role.interface';
import { NavigationItem, NavigationOption } from '../layout/navigation.model';
import { RRHH_MODAL_ARCHIVO } from './rrhh-modal-permissions.constants';

export const RRHH_ADMIN_SECTION_GRANULAR_KEYS: readonly string[] = [
  'admin.organizacion.ver',
  'admin.epp.ver',
  'admin.dispositivos.ver',
  'admin.config-dispositivos.ver',
  'admin.horarios-laborales.ver',
  'admin.config-ip-app.ver',
];

@Injectable()
export class RrhhPermissionsService {
  private readonly userDataService = inject(UserDataService);
  private readonly rolesService = inject(RolesService);

  /** Primera lectura de roles (`utils/userTypes`) reutilizada entre comprobaciones en la sesión. */
  private readonly allRolesSnapshot$ = defer(() => this.rolesService.getRoles().pipe(take(1))).pipe(
    shareReplay({ bufferSize: 1, refCount: false })
  );

  hasRrhhModuleAccess(): Observable<boolean> {
    return this.withAuthContext((userRoles, allRoles) => this.hasRrhhModuleAccessWithRoles(userRoles, allRoles));
  }

  canAccess(granularKey: string): Observable<boolean> {
    return this.withAuthContext((userRoles, allRoles) => this.canAccessWithRoles(granularKey, userRoles, allRoles));
  }

  canAccessAnyOf(granularKeys: string[]): Observable<boolean> {
    if (granularKeys.length === 0) return of(false);
    return this.withAuthContext((userRoles, allRoles) => this.canAccessAnyOfWithRoles(granularKeys, userRoles, allRoles));
  }

  /**
   * Resuelve varias claves en una sola ronda (p. ej. modales / toolbars).
   * Las claves duplicadas en el array se consultan una vez.
   */
  /**
   * Permiso para abrir o actuar dentro de un modal identificado por nombre de componente (`employee-file-modal`, etc.).
   * Orden: clave específica → paraguas `modales.ver` / `modales.gestionar` → fallbacks extra (p. ej. `nomina.ver`).
   */
  canAccessModalArchivo(
    componentBaseName: string,
    mode: 'abrir' | 'gestionar',
    extraFallbacks: readonly string[] = []
  ): Observable<boolean> {
    const key =
      mode === 'abrir'
        ? RRHH_MODAL_ARCHIVO.abrir(componentBaseName)
        : RRHH_MODAL_ARCHIVO.gestionar(componentBaseName);
    const umbrella = mode === 'abrir' ? 'modales.ver' : 'modales.gestionar';
    return this.canAccessAnyOf([key, umbrella, ...extraFallbacks]);
  }

  canAccessBatch(granularKeys: readonly string[]): Observable<Record<string, boolean>> {
    const unique = [...new Set(granularKeys.filter((k): k is string => typeof k === 'string' && k.trim().length > 0))];
    if (unique.length === 0) {
      return of({});
    }
    const sources: Record<string, Observable<boolean>> = {};
    for (const k of unique) {
      sources[k] = this.canAccess(k);
    }
    return forkJoin(sources).pipe(take(1));
  }

  getHeaderActionsPermissions(): Observable<{
    appAlerts: boolean;
    sendDocsToThirdParties: boolean;
    operations: boolean;
    audit: boolean;
    notifications: boolean;
    timeControlAlerts: boolean;
  }> {
    return forkJoin({
      user: this.userDataService.getCurrentUser().pipe(take(1)),
      allRoles: this.allRolesSnapshot$,
    }).pipe(
      map(({ user, allRoles }) => {
        if (!user) {
          return {
            appAlerts: false,
            sendDocsToThirdParties: false,
            operations: false,
            audit: false,
            notifications: false,
            timeControlAlerts: false,
          };
        }
        const userRoles = this.userDataService.normalizeUserRoles(user);
        const can = (k: string) => this.canAccessWithRoles(k, userRoles, allRoles);
        const canAny = (keys: string[]) => this.canAccessAnyOfWithRoles(keys, userRoles, allRoles);
        return {
          appAlerts: can('panel.alertas-app.ver'),
          sendDocsToThirdParties: can('documentos.terceros.enviar'),
          operations: can('panel.operaciones-pendientes.ver'),
          audit: canAny(['auditoria-archivos.ver', 'auditoria-archivos.gestionar']),
          notifications: can('panel.notificaciones.ver'),
          timeControlAlerts: canAny([
            'panel.alertas-control-horario.ver',
            'panel.alertas-control-horario.gestionar',
          ]),
        };
      }),
      take(1)
    );
  }

  hasRrhhAdministrationSectionAccess(): Observable<boolean> {
    return this.canAccessAnyOf([...RRHH_ADMIN_SECTION_GRANULAR_KEYS]);
  }

  getNavigationFilter(
    baseItems: readonly NavigationItem[],
    baseMenuOptions: Record<string, readonly NavigationOption[]>
  ): Observable<{ items: NavigationItem[]; menuOptions: Record<string, NavigationOption[]> }> {
    return forkJoin({
      user: this.userDataService.getCurrentUser().pipe(take(1)),
      allRoles: this.allRolesSnapshot$,
    }).pipe(
      map(({ user, allRoles }) => {
        if (!user) {
          return { items: [] as NavigationItem[], menuOptions: {} as Record<string, NavigationOption[]> };
        }
        const userRoles = this.userDataService.normalizeUserRoles(user);
        const can = (k: string) => this.canAccessWithRoles(k, userRoles, allRoles);
        const canAny = (keys: string[]) => this.canAccessAnyOfWithRoles(keys, userRoles, allRoles);
        const a = {
          home: this.hasRrhhModuleAccessWithRoles(userRoles, allRoles),
          payroll: can('nomina.ver'),
          staff: can('staff.ver'),
          employeesEvents: can('personal-eventos.ver'),
          personal: canAny(['nomina.ver', 'staff.ver', 'personal-eventos.ver']),
          ppe: canAny([
            'epp.ver',
            'epp.stock.ver',
            'epp.stock.editar',
            'epp.entrega.preparar',
            'epp.entrega.aprobar',
            'epp.entrega.realizar',
            'epp.entrega.verificar',
          ]),
          ppeDelivery: canAny([
            'epp.ver',
            'epp.entrega.preparar',
            'epp.entrega.aprobar',
            'epp.entrega.realizar',
            'epp.entrega.verificar',
          ]),
          ppeStock: canAny(['epp.ver', 'epp.stock.ver', 'epp.stock.editar']),
          empNews: can('novedades.ver'),
          empVac: can('vacaciones.ver'),
          empReq: canAny(['solicitudes.ver', 'anticipos.ver']),
          empTime: can('control-horario.ver'),
          empHab: can('haberes.ver'),
          empEv: can('eventuales.ver'),
          empTrain: can('capacitaciones.ver'),
          empEvaluations: can('evaluaciones.ver'),
          employeesManagement: canAny([
            'novedades.ver',
            'vacaciones.ver',
            'solicitudes.ver',
            'control-horario.ver',
            'haberes.ver',
            'eventuales.ver',
            'capacitaciones.ver',
            'evaluaciones.ver',
          ]),
          docEmpresa: can('documentos.empresa.ver'),
          docVeh: can('documentos.vehiculos.ver'),
          docHse: can('documentos.seguridad-higiene.ver'),
          docBpm: canAny(['documentos.buenas-practicas.ver', 'documentos.bpm.ver']),
          docSst: can('documentos.medico-laboral.ver'),
          documents: canAny([
            'documentos.empresa.ver',
            'documentos.vehiculos.ver',
            'documentos.seguridad-higiene.ver',
            'documentos.buenas-practicas.ver',
            'documentos.bpm.ver',
            'documentos.medico-laboral.ver',
          ]),
          adminPeople: can('admin.organizacion.ver'),
          adminEpp: can('admin.epp.ver'),
          adminDev: can('admin.dispositivos.ver'),
          adminDevCfg: can('admin.config-dispositivos.ver'),
          adminSched: can('admin.horarios-laborales.ver'),
          adminIp: can('admin.config-ip-app.ver'),
          admin: canAny([...RRHH_ADMIN_SECTION_GRANULAR_KEYS]),
        };
        const items: NavigationItem[] = [];
        for (const item of baseItems) {
          let include = false;
          switch (item.id) {
            case 'home':
              include = a.home;
              break;
            case 'personal':
              include = a.personal;
              break;
            case 'ppe':
              include = a.ppe;
              break;
            case 'employees-management':
              include = a.employeesManagement;
              break;
            case 'documents':
              include = a.documents;
              break;
            case 'admin':
              include = a.admin;
              break;
            default:
              include = false;
          }
          if (include) {
            items.push({ ...item, hasOptions: item.hasOptions });
          }
        }

        const menuOptions: Record<string, NavigationOption[]> = {};

        const filterOpts = (key: string, flags: Record<string, boolean>): NavigationOption[] => {
          const src = baseMenuOptions[key] ?? [];
          return src.filter((opt) => flags[opt.id] === true);
        };

        if (a.personal) {
          menuOptions['personal'] = filterOpts('personal', {
            'personal-payroll': a.payroll,
            'personal-staff': a.staff,
            'personal-events': a.employeesEvents,
          });
        }

        if (a.ppe) {
          menuOptions['ppe'] = filterOpts('ppe', {
            'ppe-delivery': a.ppeDelivery,
            'ppe-stock': a.ppeStock,
          });
        }

        if (a.employeesManagement) {
          menuOptions['employees-management'] = filterOpts('employees-management', {
            'employees-news': a.empNews,
            'employees-vacations': a.empVac,
            'employees-requests': a.empReq,
            'employees-time': a.empTime,
            'employees-haberes': a.empHab,
            'employees-eventuales': a.empEv,
            'employees-training': a.empTrain,
            'employees-evaluations': a.empEvaluations,
          });
        }

        if (a.documents) {
          menuOptions['documents'] = filterOpts('documents', {
            'doc-empresa': a.docEmpresa,
            'doc-vehiculos': a.docVeh,
            'doc-seguridad-higiene': a.docHse,
            'doc-buenas-practicas': a.docBpm,
            'doc-medico-laboral': a.docSst,
          });
        }

        if (a.admin) {
          menuOptions['admin'] = filterOpts('admin', {
            'admin-people': a.adminPeople,
            'admin-epp': a.adminEpp,
            'admin-devices': a.adminDev,
            'admin-device-config': a.adminDevCfg,
            'admin-work-schedules': a.adminSched,
            'admin-config-ip-app': a.adminIp,
          });
        }

        const itemsAdjusted = items.filter((item) => {
          if (!item.hasOptions) return true;
          const opts = menuOptions[item.id];
          return opts && opts.length > 0;
        });

        return { items: itemsAdjusted, menuOptions };
      }),
      take(1)
    );
  }

  private withAuthContext(project: (userRoles: string[], allRoles: Role[]) => boolean): Observable<boolean> {
    return forkJoin({
      user: this.userDataService.getCurrentUser().pipe(take(1)),
      allRoles: this.allRolesSnapshot$,
    }).pipe(
      map(({ user, allRoles }) => {
        if (!user) return false;
        const userRoles = this.userDataService.normalizeUserRoles(user);
        return project(userRoles, allRoles);
      }),
      take(1)
    );
  }

  private hasRrhhModuleAccessWithRoles(userRoles: string[], allRoles: Role[]): boolean {
    if (this.isSysAdmin(userRoles, allRoles)) return true;
    return this.hasGranularPermission('ver', userRoles, allRoles) || this.getRrhhGranularPermissions(userRoles, allRoles).length > 0;
  }

  private canAccessWithRoles(granularKey: string, userRoles: string[], allRoles: Role[]): boolean {
    if (this.isSysAdmin(userRoles, allRoles)) return true;
    if (granularKey === 'ver') return this.hasRrhhModuleAccessWithRoles(userRoles, allRoles);
    return this.hasGranularPermission(granularKey, userRoles, allRoles);
  }

  private canAccessAnyOfWithRoles(keys: string[], userRoles: string[], allRoles: Role[]): boolean {
    return keys.some((k) => this.canAccessWithRoles(k, userRoles, allRoles));
  }

  private isSysAdmin(userRoles: string[], allRoles: Role[]): boolean {
    for (const roleName of userRoles) {
      const role = allRoles.find((r) => r.name === roleName);
      if (!role) continue;
      if ((role.name || '').trim().toLowerCase() === 'sysadmin') {
        return true;
      }
    }
    return false;
  }

  private hasGranularPermission(key: string, userRoles: string[], allRoles: Role[]): boolean {
    return this.getRrhhGranularPermissions(userRoles, allRoles).includes(key);
  }

  private getRrhhGranularPermissions(userRoles: string[], allRoles: Role[]): string[] {
    const perms = new Set<string>();
    for (const roleName of userRoles) {
      const role = allRoles.find((r) => r.name === roleName);
      if (!role) continue;
      const rrhh = role.granularPermissions?.find((gp) => gp.module === 'rrhh');
      (rrhh?.permissions || []).forEach((p) => perms.add(p));
    }
    return Array.from(perms);
  }

  isCurrentUserSysAdmin(): Observable<boolean> {
    return this.withAuthContext((userRoles, allRoles) => this.isSysAdmin(userRoles, allRoles));
  }
}
